import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Features = () => {
  var [activeCategory, setActiveCategory] = useState('all');

  var categories = [
    { id: 'all', label: 'All Features' },
    { id: 'booking', label: 'Booking & Scheduling' },
    { id: 'crm', label: 'Customer Management' },
    { id: 'team', label: 'Team & Staff' },
    { id: 'inventory', label: 'Inventory & POS' },
    { id: 'jobs', label: 'Jobs & Invoicing' },
    { id: 'marketing', label: 'Marketing & Growth' },
    { id: 'payments', label: 'Payments & Billing' },
    { id: 'website', label: 'Website & Branding' },
  ];

  var features = [
    // BOOKING
    { cat: 'booking', icon: '\u{1F4C5}', title: 'Smart Booking Calendar',
      desc: 'Manage all appointments in one place. Drag to reschedule, color-coded by service, conflict detection built in.',
      highlights: ['Drag & drop rescheduling', 'Conflict detection', 'Status tracking', 'Complete / cancel / no-show'],
      path: '/bookings', status: 'live' },
    { cat: 'booking', icon: '\u{1F310}', title: 'Online Booking Page',
      desc: 'Your customers book 24/7 from their phone. Branded page with categorized services, staff preferences, and real-time availability.',
      highlights: ['Mobile-first design', 'Categorized services', 'Staff preference', 'Search & filter', 'Instant confirmation'],
      path: null, status: 'live' },
    { cat: 'booking', icon: '\u{1F4AC}', title: 'Automated Reminders',
      desc: 'Reduce no-shows by up to 40%. Automatic SMS and email reminders before every appointment. Fully customizable.',
      highlights: ['SMS + email reminders', 'Customizable timing', 'Custom templates', 'No-show tracking'],
      path: '/reminders', status: 'live' },
    { cat: 'booking', icon: '\u{1F4CB}', title: 'Waitlist Management',
      desc: 'When a slot opens up, your waitlisted customers get notified automatically. Never lose a booking opportunity.',
      highlights: ['Auto-notification on cancel', 'Priority ordering', 'SMS alerts', 'One-click booking'],
      path: '/waitlist', status: 'live' },
    { cat: 'booking', icon: '\u{1F4DD}', title: 'Digital Intake Forms',
      desc: 'Paperless check-in. Customers fill out health forms, preferences, and waivers before they arrive.',
      highlights: ['Custom form builder', 'Pre-visit link', 'Auto-save to profile'],
      path: null, status: 'planned' },

    // CRM
    { cat: 'crm', icon: '\u{1F465}', title: 'Customer CRM',
      desc: 'Know every customer. Visit history, spending patterns, preferences, notes, and contact details all in one profile.',
      highlights: ['Full visit history', 'Spending analytics', 'Tags & segments', 'CSV import'],
      path: '/customers', status: 'live' },
    { cat: 'crm', icon: '\u{1F4F7}', title: 'Client Photos',
      desc: 'Store before/after photos, progress shots, and reference images directly on customer profiles.',
      highlights: ['Photo uploads', 'Before & after', 'Linked to profile', 'Gallery view'],
      path: '/client-photos', status: 'live' },
    { cat: 'crm', icon: '\u{2B50}', title: 'Review Requests',
      desc: 'Automatically request Google reviews after every checkout. Build your online reputation on autopilot.',
      highlights: ['Auto-send after checkout', 'SMS delivery', 'Google review link', 'Notification log'],
      path: null, status: 'live' },
    { cat: 'crm', icon: '\u{1F4E8}', title: 'Notification Log',
      desc: 'See every SMS and email your business has sent. Full audit trail with status, recipient, and message content.',
      highlights: ['SMS + email tracking', 'Sent / failed status', 'Filter & search', 'Expandable details'],
      path: '/notifications', status: 'live' },

    // TEAM
    { cat: 'team', icon: '\u{1F454}', title: 'Staff Management',
      desc: 'Add your team, assign roles, set schedules, and track performance. Everyone knows where they need to be.',
      highlights: ['Role-based access', 'Service assignments', 'Performance tracking', 'Active/inactive toggle'],
      path: '/staff', status: 'live' },
    { cat: 'team', icon: '\u{1F5D3}', title: 'Staff Calendar & Scheduling',
      desc: 'Visual calendar showing who works when. Drag to create shifts, spot coverage gaps, manage time off.',
      highlights: ['Visual shift builder', 'Coverage gap alerts', 'Time-off requests'],
      path: '/schedule', status: 'live' },
    { cat: 'team', icon: '\u{1F4B5}', title: 'Commission Tracking',
      desc: 'Automatically calculate staff commissions by service type. No more spreadsheets at end of month.',
      highlights: ['Per-service rates', 'Auto-calculate', 'Monthly payout reports'],
      path: '/commissions', status: 'live' },
    { cat: 'team', icon: '\u{1F510}', title: 'Employee Portal',
      desc: 'Staff clock in with a PIN. View their own schedule, commissions, and upcoming appointments.',
      highlights: ['PIN-based login', 'Personal schedule', 'Commission view', 'Clock in/out'],
      path: null, status: 'live' },

    // INVENTORY & POS
    { cat: 'inventory', icon: '\u{1F4E6}', title: 'Product Inventory',
      desc: 'Track every product on your shelves. Categories, cost price, retail price, and real-time stock levels.',
      highlights: ['Product catalog', 'Categories', 'Cost & retail pricing', 'Stock levels', 'CSV import'],
      path: '/inventory', status: 'live' },
    { cat: 'inventory', icon: '\u{1F514}', title: 'Low Stock Alerts',
      desc: 'Never run out of your best sellers. Automatic alerts when products hit reorder thresholds.',
      highlights: ['Custom reorder points', 'Dashboard alerts', 'Stock tracking'],
      path: null, status: 'live' },
    { cat: 'inventory', icon: '\u{1F4CB}', title: 'CSV Import System',
      desc: 'Bulk import customers, services, inventory, and staff from spreadsheets. Map columns and import in seconds.',
      highlights: ['Customers, services, inventory, staff', 'Column mapping', 'Validation & preview', 'Error handling'],
      path: '/import', status: 'live' },

    // JOBS & INVOICING
    { cat: 'jobs', icon: '\u{1F527}', title: 'Work Orders',
      desc: 'Kanban-style job pipeline. Create work orders, track through Received, Diagnosed, Approved, In Progress, QC, and Ready.',
      highlights: ['Kanban pipeline', '6-stage workflow', 'Line items (labor + parts)', 'Customer SMS on ready', 'Staff assignment'],
      path: '/work-orders', status: 'live' },
    { cat: 'jobs', icon: '\u{1F4B0}', title: 'Estimates & Quotes',
      desc: 'Send professional estimates before the work starts. Customer approves, converts to work order with one click.',
      highlights: ['Line item builder', 'Status tracking', 'Convert to work order', 'Pending / approved / declined'],
      path: '/estimates', status: 'live' },
    { cat: 'jobs', icon: '\u{1F4C4}', title: 'Invoicing',
      desc: 'Generate invoices from completed work orders or custom line items. Track payments and outstanding balances.',
      highlights: ['Auto-generate from work order', 'Payment tracking', 'Outstanding balance', 'Status workflow'],
      path: '/invoices', status: 'live' },
    { cat: 'jobs', icon: '\u{1F697}', title: 'Vehicle / Asset Profiles',
      desc: 'Store customer vehicles with VIN, make, model, year, and full service history. One tap to see everything done.',
      highlights: ['VIN / serial number', 'Make / model / year', 'Full service history', 'Linked to customer'],
      path: '/vehicles', status: 'live' },
    { cat: 'jobs', icon: '\u{1F50D}', title: 'Track My Bike / Order',
      desc: 'Public tracking page where customers check work order status in real-time. No login required.',
      highlights: ['Public URL', 'Real-time status', 'No login needed', 'Business branded'],
      path: null, status: 'live' },

    // MARKETING
    { cat: 'marketing', icon: '\u{1F4F1}', title: 'SMS Notifications',
      desc: 'Automated texts for appointment reminders, work order updates, review requests, and waitlist alerts.',
      highlights: ['Twilio integration', 'Auto-trigger on events', 'Notification log', 'Delivery tracking'],
      path: null, status: 'live' },
    { cat: 'marketing', icon: '\u{1F4E7}', title: 'Email Receipts',
      desc: 'Professional branded receipts sent automatically after every checkout. Service details, tip, and total.',
      highlights: ['Auto-send on checkout', 'Branded template', 'Service breakdown', 'Book again CTA'],
      path: null, status: 'live' },
    { cat: 'marketing', icon: '\u{1F916}', title: 'Smart Automations',
      desc: 'Set it and forget it. Auto-text new customers, follow up after visits, win back inactive customers.',
      highlights: ['Welcome series', 'Post-visit follow-up', 'Win-back campaigns', 'Birthday deals'],
      path: null, status: 'planned' },
    { cat: 'marketing', icon: '\u{1F39F}', title: 'SMS Campaigns',
      desc: 'Send bulk texts to all customers or targeted segments. Promotions, holiday specials, slow-day flash deals.',
      highlights: ['Audience segments', 'Templates library', 'Delivery tracking'],
      path: null, status: 'coming' },

    // PAYMENTS
    { cat: 'payments', icon: '\u{1F4B3}', title: 'Quick Checkout',
      desc: 'Close out in seconds. Tip calculator, discounts, tax, and payment tracking. Works for walk-ins and appointments.',
      highlights: ['One-tap checkout', 'Tip calculation', 'Discounts & tax', 'Receipt auto-send'],
      path: '/checkout', status: 'live' },
    { cat: 'payments', icon: '\u{1F4CA}', title: 'Revenue Reports',
      desc: 'See how your business is doing. Revenue trends, busiest hours, top services, and staff performance.',
      highlights: ['Daily/weekly/monthly', 'Service popularity', 'Staff performance', 'Export data'],
      path: '/reports', status: 'live' },
    { cat: 'payments', icon: '\u{1F381}', title: 'Gift Cards',
      desc: 'Sell digital gift cards. Customers purchase, recipients redeem at checkout. Track balances automatically.',
      highlights: ['Custom amounts', 'Balance tracking', 'Redeem at checkout', 'Gift card codes'],
      path: '/gift-cards', status: 'live' },
    { cat: 'payments', icon: '\u{1F504}', title: 'Memberships',
      desc: 'Recurring revenue on autopilot. Create monthly membership plans with included services and perks.',
      highlights: ['Monthly plans', 'Included services', 'Member pricing', 'Usage tracking'],
      path: '/memberships', status: 'live' },
    { cat: 'payments', icon: '\u{1F4E6}', title: 'Service Packages',
      desc: 'Bundle services into discounted packages. Customers buy upfront, redeem over multiple visits.',
      highlights: ['Bundle services', 'Discounted pricing', 'Usage tracking', 'Expiration dates'],
      path: '/packages', status: 'live' },

    // WEBSITE
    { cat: 'website', icon: '\u{1F3A8}', title: 'Website Builder',
      desc: 'Professional business website with zero code. Choose templates, colors, fonts. Services, team, reviews, contact \u2014 all dynamic.',
      highlights: ['Multiple templates', 'Custom branding', 'Mobile responsive', 'SEO settings', 'Drag-drop images'],
      path: '/site', status: 'live' },
    { cat: 'website', icon: '\u{1F517}', title: 'Custom Subdomain',
      desc: 'Your business gets its own URL. Share your booking link, website, and tracking pages \u2014 all branded to you.',
      highlights: ['Unique URL', 'Booking page', 'Public website', 'Tracking page'],
      path: null, status: 'live' },
    { cat: 'website', icon: '\u{1F4F1}', title: 'Mobile-First Design',
      desc: 'Every page \u2014 dashboard, booking, website, tracking \u2014 is optimized for mobile. Your staff and customers use it on any device.',
      highlights: ['Responsive dashboard', 'Mobile booking', 'Touch-friendly', 'Works on any device'],
      path: null, status: 'live' },
  ];

  var filtered = activeCategory === 'all' ? features : features.filter(function(f) { return f.cat === activeCategory; });
  var liveCount = features.filter(function(f) { return f.status === 'live'; }).length;
  var comingCount = features.filter(function(f) { return f.status === 'coming'; }).length;
  var plannedCount = features.filter(function(f) { return f.status === 'planned'; }).length;
  var totalCount = features.length;

  var statusConfig = {
    live: { label: 'Live', bg: '#00b894', glow: '0 0 12px #00b89440' },
    coming: { label: 'Coming Soon', bg: '#ffab40', glow: '0 0 12px #ffab4040' },
    planned: { label: 'On Roadmap', bg: '#6c5ce7', glow: '0 0 12px #6c5ce740' },
  };

  return (
    <div style={{ minHeight: '100vh', padding: '0 24px 60px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', padding: '48px 0 40px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'linear-gradient(135deg, #ff6b2b15, #ffab4015)', border: '1px solid #ff6b2b30', padding: '8px 20px', borderRadius: '100px', fontSize: '14px', color: '#ff6b2b', fontWeight: '600', marginBottom: '20px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00b894', boxShadow: '0 0 6px #00b894' }}></span>
          {liveCount} live &middot; {comingCount + plannedCount} coming &middot; {totalCount} total
        </div>
        <h1 style={{ color: '#fff', fontSize: '42px', fontWeight: '800', letterSpacing: '-1.5px', lineHeight: '1.15', margin: '0 0 16px', background: 'linear-gradient(135deg, #fff 0%, #ccc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          One platform to run<br />your entire business
        </h1>
        <p style={{ color: '#888', fontSize: '17px', lineHeight: '1.7', maxWidth: '600px', margin: '0 auto 32px' }}>
          Bookings, CRM, work orders, invoicing, inventory, website builder, staff management, payments, SMS &mdash; everything a service business needs. Starting at $99/month.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {Object.entries(statusConfig).map(function(entry) {
            return (
              <div key={entry[0]} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: entry[1].bg, boxShadow: entry[1].glow }}></div>
                <span style={{ color: '#888', fontSize: '13px' }}>{entry[1].label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto 32px' }}>
        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '4px', background: '#252840', borderRadius: '14px' }}>
          {categories.map(function(cat) {
            var count = cat.id === 'all' ? features.length : features.filter(function(f) { return f.cat === cat.id; }).length;
            return (
              <button key={cat.id} onClick={function() { setActiveCategory(cat.id); }} style={{
                flex: '0 0 auto', padding: '11px 22px', borderRadius: '10px', border: 'none',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                background: activeCategory === cat.id ? 'linear-gradient(135deg, #ff6b2b, #ffab40)' : 'transparent',
                color: activeCategory === cat.id ? '#fff' : '#888',
              }}>{cat.label} <span style={{ opacity: 0.6, fontSize: '12px' }}>({count})</span></button>
            );
          })}
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '18px' }}>
        {filtered.map(function(f, i) {
          var sc = statusConfig[f.status];
          return (
            <div key={i} style={{ background: '#12121f', borderRadius: '16px', padding: '28px', border: '1px solid #1e1e35', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: f.status === 'live' ? 'linear-gradient(90deg, #ff6b2b, #ffab40)' : f.status === 'coming' ? 'linear-gradient(90deg, #ffab40, #fdcb6e)' : 'linear-gradient(90deg, #6c5ce7, #a29bfe)', opacity: f.status === 'live' ? 1 : 0.5 }}></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#1e1e35', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>{f.icon}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: sc.bg + '18', color: sc.bg }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: sc.bg, boxShadow: sc.glow }}></div>
                  {sc.label}
                </div>
              </div>
              <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', margin: '0 0 8px', letterSpacing: '-0.3px' }}>{f.title}</h3>
              <p style={{ color: '#777', fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px' }}>{f.desc}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: f.path ? '16px' : '0' }}>
                {f.highlights.map(function(h, j) {
                  return (<span key={j} style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '12px', background: '#252840', color: '#aaa', fontWeight: '500' }}>{h}</span>);
                })}
              </div>
              {f.path && f.status === 'live' && (
                <Link to={'/app' + f.path} style={{ color: '#ff6b2b', fontSize: '13px', fontWeight: '700', textDecoration: 'none' }}>
                  Try it now &rarr;
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ maxWidth: '1100px', margin: '60px auto 0' }}>
        <div style={{ background: '#12121f', borderRadius: '20px', padding: '48px', border: '1px solid #1e1e35', textAlign: 'center' }}>
          <h2 style={{ color: '#fff', fontSize: '26px', fontWeight: '700', margin: '0 0 8px' }}>Built for every service business</h2>
          <p style={{ color: '#888', fontSize: '15px', margin: '0 0 32px' }}>One platform, any industry. Terminology and features adapt to your business type.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px' }}>
            {['Massage & Spa', 'Motorcycle Shops', 'Barbershops', 'Nail Salons', 'Med Spas', 'Smoke & Vape Shops', 'Auto Repair', 'Pet Grooming', 'Fitness Studios', 'Tattoo Studios', 'Cleaning Services', 'Hair Salons', 'Repair Shops', 'Mobile Services'].map(function(biz, i) {
              return (<span key={i} style={{ padding: '10px 20px', borderRadius: '10px', fontSize: '14px', background: '#252840', color: '#ccc', fontWeight: '500', border: '1px solid #252540' }}>{biz}</span>);
            })}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '40px auto 0' }}>
        <div style={{ background: 'linear-gradient(135deg, #ff6b2b10, #ffab4005)', borderRadius: '20px', padding: '48px', border: '1px solid #ff6b2b20', textAlign: 'center' }}>
          <h2 style={{ color: '#fff', fontSize: '26px', fontWeight: '700', margin: '0 0 8px' }}>One plan. Everything included.</h2>
          <p style={{ color: '#888', fontSize: '15px', margin: '0 0 36px' }}>No setup fees. No contracts. No feature gates. Cancel anytime.</p>
          <div style={{ maxWidth: '480px', margin: '0 auto' }}>
            <div style={{ background: '#12121f', borderRadius: '16px', padding: '36px 28px', border: '2px solid #ff6b2b40', textAlign: 'center' }}>
              <div style={{ color: '#ff6b2b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Founding Member</div>
              <div style={{ color: '#fff', fontSize: '56px', fontWeight: '800', letterSpacing: '-2px', marginBottom: '4px' }}>$99<span style={{ fontSize: '18px', color: '#666', fontWeight: '500' }}>/mo</span></div>
              <div style={{ color: '#888', fontSize: '14px', marginBottom: '24px' }}>Unlimited staff &middot; 3,000 SMS/mo &middot; +$49/extra location</div>
              <div style={{ borderTop: '1px solid #1e1e35', paddingTop: '20px', textAlign: 'left' }}>
                {['Online booking & branded website', 'Customer CRM & contact management', 'Calendar & staff scheduling', 'Checkout & payments (Stripe)', 'Automated reminders (SMS + email)', 'Work orders (kanban board)', 'Estimates & invoices', 'Vehicle tracking & history', 'Inventory management', 'SMS & email marketing campaigns', 'Memberships & recurring billing', 'Gift cards & promo codes', 'Commission tracking & payroll reports', 'Employee portal (PIN login)', 'Reports & advanced analytics', 'Customer portal & shop queue TV', 'CSV import tool'].map(function(f, j) {
                  return (
                    <div key={j} style={{ padding: '5px 0', fontSize: '13px', color: '#aaa', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#00b894', fontSize: '11px' }}>{'\u2713'}</span>
                      {f}
                    </div>
                  );
                })}
              </div>
              <Link to="/login" style={{ display: 'block', marginTop: '24px', padding: '14px', background: '#ff6b2b', color: '#fff', borderRadius: '12px', fontSize: '15px', fontWeight: '700', textDecoration: 'none' }}>Get Started</Link>
            </div>
          </div>
          <p style={{ color: '#f59e0b', fontSize: '13px', marginTop: '20px', fontWeight: '600' }}>
            First 10 businesses lock in $99/mo forever. Price goes to $199/mo after.
          </p>
          <p style={{ color: '#666', fontSize: '13px', marginTop: '8px' }}>
            14-day free trial &middot; No credit card required
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '600px', margin: '40px auto 0', textAlign: 'center' }}>
        <h2 style={{ color: '#fff', fontSize: '22px', fontWeight: '700', margin: '0 0 8px' }}>Ready to simplify your business?</h2>
        <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>Join businesses already using IVision Marketing to save time and grow revenue.</p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/login" style={{ padding: '14px 32px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', color: '#fff', borderRadius: '12px', fontSize: '15px', fontWeight: '700', textDecoration: 'none' }}>Get Started</Link>
          <a href="mailto:ivanjackm@protonmail.com" style={{ padding: '14px 32px', background: '#1e1e35', border: '1px solid #2a2a4a', color: '#ccc', borderRadius: '12px', fontSize: '15px', fontWeight: '600', textDecoration: 'none' }}>Contact Sales</a>
        </div>
      </div>
    </div>
  );
};

export default Features;
